import { useEffect, useRef, RefObject } from 'react';

/**
 * Attaches an IntersectionObserver to the referenced element.
 * When the element enters the viewport, the class 'in' is added,
 * triggering the CSS transition defined on .reveal.
 *
 * Respects prefers-reduced-motion: if motion is reduced, the observer is
 * never created (the CSS fallback already makes .reveal visible).
 *
 * Also marks elements already in the viewport at mount time as visible,
 * which handles the case where language change triggers a full re-render
 * while the user has scrolled past a section.
 */
export function useRevealOnScroll<T extends HTMLElement>(): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = ref.current;
    if (!el) return;

    // If already in viewport at mount time, reveal immediately
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      el.classList.add('in');
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return ref;
}
